// Client-side mirror of the server's permission-grant expansion (PermissionSet.From).
// The Roles editor stores/reads CONCRETE tokens (feature:k:read|write|purge,
// content:type:read|write|publish|promote); this expands a role's compact grants (incl. wildcards
// `*`, `feature:*:write`, `content:*:write`, `content:@group:write`) into that concrete set so the
// checkbox matrix reflects built-in roles correctly. Write implies read; purge implies read; write
// does NOT imply purge. Verbes de déploiement (content-only) : promote implique publish qui
// implique read ; write n'implique JAMAIS publish/promote. `feature:*:purge` n'existe pas côté
// serveur : la purge n'est jamais émise qu'en jetons concrets, uniquement pour les fonctionnalités
// qui la supportent. Tout verbe inconnu de ce miroir serait PERDU à la ré-sauvegarde d'un rôle
// (bug purge historique) : garder ce fichier aligné sur PermissionSet.cs.
import type { CatalogFeature, PermissionCatalog } from "./PermissionsContext";

export function expandGrants(catalog: PermissionCatalog, grants: string[]): Set<string> {
	const out = new Set<string>();
	const addFeature = (key: string, action: string) => {
		out.add(`feature:${key}:read`);
		if (action === "write") out.add(`feature:${key}:write`);
		if (action === "purge") out.add(`feature:${key}:purge`);
	};
	const addContent = (key: string, action: string) => {
		// Échelles : write ⇒ read ; promote ⇒ publish ⇒ read (write et publish orthogonaux).
		out.add(`content:${key}:read`);
		if (action === "write") out.add(`content:${key}:write`);
		if (action === "publish" || action === "promote") out.add(`content:${key}:publish`);
		if (action === "promote") out.add(`content:${key}:promote`);
	};

	for (const raw of grants ?? []) {
		const g = (raw ?? "").trim();
		if (!g) continue;

		if (g === "*") {
			for (const f of catalog.features) {
				addFeature(f.key, "read");
				if (f.supportsWrite) addFeature(f.key, "write");
				// La purge du joker : des clés concrètes uniquement : jamais un `feature:*:purge`.
				if (f.supportsPurge === true) addFeature(f.key, "purge");
			}
			for (const t of catalog.contentTypes) {
				addContent(t.key, "write");
				// Le super-admin détient aussi les verbes de déploiement (miroir de PermissionSet).
				addContent(t.key, "promote");
			}
			continue;
		}

		const [resource, sel, action] = g.split(":");
		if (
			action !== "read" &&
			action !== "write" &&
			action !== "purge" &&
			action !== "publish" &&
			action !== "promote"
		)
			continue;
		// publish/promote sont content-only : un feature:x:publish reste mort.
		if ((action === "publish" || action === "promote") && resource !== "content") continue;

		if (resource === "feature") {
			const apply = (f: CatalogFeature) => {
				if (action === "write" && f.supportsWrite) addFeature(f.key, "write");
				else if (action === "purge" && f.supportsPurge === true) addFeature(f.key, "purge");
				else addFeature(f.key, "read");
			};
			if (sel === "*") catalog.features.forEach(apply);
			else {
				const f = catalog.features.find((x) => x.key === sel);
				if (f) apply(f);
			}
		} else if (resource === "content") {
			if (action === "purge") continue; // les types de contenu n'ont pas d'action purge
			if (sel === "*") catalog.contentTypes.forEach((t) => addContent(t.key, action));
			else if (sel.startsWith("@")) {
				const grp = catalog.groups.find((x) => x.key === sel.slice(1));
				if (grp) grp.typeKeys.forEach((tk) => addContent(tk, action));
			} else if (catalog.contentTypes.some((t) => t.key === sel)) {
				addContent(sel, action);
			}
		}
	}
	return out;
}

/** Toggle a concrete token in a token set, maintaining the implication ladders:
 *  write⇒read, purge⇒read, promote⇒publish⇒read. Unchecking read clears EVERY stronger verb
 *  (write, purge, publish, promote) ; unchecking publish clears promote ; write, purge and the
 *  deploy verbs stay independent of each other. */
export function toggleToken(
	tokens: Set<string>,
	resource: "feature" | "content",
	key: string,
	action: "read" | "write" | "purge" | "publish" | "promote",
	on: boolean
): Set<string> {
	const next = new Set(tokens);
	const read = `${resource}:${key}:read`;
	const write = `${resource}:${key}:write`;
	const purge = `${resource}:${key}:purge`;
	const publish = `${resource}:${key}:publish`;
	const promote = `${resource}:${key}:promote`;
	if (action === "read") {
		if (on) next.add(read);
		else {
			next.delete(read);
			next.delete(write); // can't have write without read
			next.delete(purge); // …nor purge without read
			next.delete(publish); // …nor any deploy verb without read
			next.delete(promote);
		}
	} else if (action === "write") {
		if (on) {
			next.add(write);
			next.add(read);
		} else {
			next.delete(write);
		}
	} else if (action === "publish") {
		if (on) {
			next.add(publish);
			next.add(read); // publish implies read
		} else {
			next.delete(publish);
			next.delete(promote); // can't promote without publish
		}
	} else if (action === "promote") {
		if (on) {
			next.add(promote);
			next.add(publish); // promote implies publish…
			next.add(read); // …which implies read
		} else {
			next.delete(promote);
		}
	} else {
		if (on) {
			next.add(purge);
			next.add(read); // purge implies read
		} else {
			next.delete(purge);
		}
	}
	return next;
}
